import eventbus from 'tiny-emitter/instance';
import { TYPE, GRIDSNAP } from '@/constants';
import ShapeHelper from '../models/shapes/index';
import { GAHelper } from '../models/ga/index';

const availableTypes = [TYPE.CIRCLE, TYPE.RECT, TYPE.ELLIPSE, TYPE.LINE, TYPE.POLYGON];

export default class DrawingSimpleShapes {
  mouseListenerOptions = {
    passive: false,
    capture: false,
  };

  coords = Object.create(null);

  constructor(app, type, ga = false) {
    if (availableTypes.indexOf(type) === -1) {
      throw Error(`Shape type is not supported: ${type}`);
    }
    this.app = app;
    this.ga = ga;
    this.canvas = this.app.canvas;
    this.svg = this.app.svg;
    this.type = type;
    this.setup();
    this.setupEventListeners();

    return this;
  }

  setup() {
    switch (this.type) {
      case TYPE.POLYGON:
        this.initPolygonDrawing();
        break;
      case TYPE.LINE:
      default:
        this.initSimpleDrawing();
    }
    this.svg.attr({ cursor: 'crosshair' });
  }

  setupEventListeners() {
    this.app.svg.on('keydown', this.handleKeyDown, this);
  }

  initSimpleDrawing() {
    this.addStandardEventListeners();
  }

  initPolygonDrawing() {
    this.initDrawingShape();
    this.shape.draw({ snapToGrid: 1 });
    this.initPolygonListeners();
  }

  initPolygonListeners() {
    this.shape.on('drawstart', () => {
      eventbus.emit('drawing:started');
      this.app.svg.on('dblclick', this.handleDblClick, this);
    });
    this.shape.on('drawstop', this.onPolygonDrawStop, this);
  }

  handleDblClick(e) {
    e.preventDefault();
    this.shape.draw('done');
    this.shape.off('drawstart');
  }

  onPolygonDrawStart() {
    eventbus.emit('drawing:started');
    this.addPolygonEventListeners();
  }

  onPolygonDrawStop() {
    this.handleDrawStop();
    this.removePolygonEventListeners();
  }

  handleDrawStop() {
    const attributes = {};
    const sid = this.app.currentSection && this.app.currentSection.uuid;
    switch (this.type) {
      case TYPE.CIRCLE:
      case TYPE.GA_ROUNDED:
        attributes.position = [this.shape.attr('cx'), this.shape.attr('cy')];
        attributes.radius = this.shape.attr('r');
        break;
      case TYPE.ELLIPSE:
        attributes.width = this.shape.width();
        attributes.height = this.shape.height();
        attributes.position = [this.shape.cx(), this.shape.cy()];
        break;
      case TYPE.RECT:
      case TYPE.GA_RECTANGULAR: {
        const { width, height } = this.shape.attr();
        attributes.position = [this.shape.cx(), this.shape.cy()];
        attributes.radius = this.shape.attr('rx');
        attributes.width = width;
        attributes.height = height;
        break;
      }
      case TYPE.LINE: {
        const { x1, y1, x2, y2 } = this.shape.attr();
        attributes.position = [this.shape.cx(), this.shape.cy()];
        attributes.shape = [x1, y1, x2, y2];
        break;
      }
      case TYPE.POLYGON:
      case TYPE.GA_POLYGONAL:
        attributes.shape = this.shape.attr('points');
        attributes.position = [this.shape.cx(), this.shape.cy()];
        break;
      default:
        break;
    }
    const selectedHelper = this.ga ? GAHelper : ShapeHelper;
    const unit = selectedHelper.create(this.type, { sid, attributes });
    eventbus.emit('seatmap:add:unit', unit);
  }

  addPolygonEventListeners() {
    this.app.svg.on('dblclick', this.handlePolygonDblclick, this);
    this.app.svg.on('keydown', this.handlePolygonKeydown, this);
    this.app.svg.on('keyup', this.handlePolygonKeyup, this);
  }

  handlePolygonDblclick() {
    this.shape.draw('stop');
    this.shape.draw('done');
  }

  handlePolygonKeydown(e) {
    // snapping to grid while holding Ctrl button
    if (e.keyCode === 17) {
      this.shape.draw('param', 'snapToGrid', GRIDSNAP);
    }
  }

  handlePolygonKeyup() {
    this.shape.draw('param', 'snapToGrid', 1);
  }

  addStandardEventListeners() {
    this.app.svg.on('mousedown', this.handleMouseDown, this);
    this.app.svg.on('mouseup', this.handleMouseUp, this);
    this.app.svg.on('mousemove', this.handleMouseMove, this);
  }

  handleKeyDown(e) {
    if (e.keyCode === 27) {
      this.shape.draw('cancel');
    }
  }

  handleMouseDown(e) {
    if (e.button !== 0) return;
    e.preventDefault();
    this.initDrawingShape();
    this.shape.draw(e);
    this.shape.on('drawstop', this.handleDrawStop, this);
    eventbus.emit('drawing:started');
  }

  handleMouseUp(e) {
    if (e.button !== 0) return;
    this.shape.draw('stop', e);
    this.shape.off('drawstop');
  }

  handleMouseMove(e) {
    e.preventDefault();
  }

  onDrawStop() {
    // do something useful
  }

  initDrawingShape() {
    this.shape = this.canvas[this.type]().addClass(`shape shape-${this.type}`);
  }

  destroyEventListeners() {
    this.removeStandardEventListeners();
    this.removePolygonEventListeners();
    this.app.svg.off('keydown', this.handleKeyDown);
  }

  removeStandardEventListeners() {
    this.app.svg.off('mousedown', this.handleMouseDown);
    this.app.svg.off('mouseup', this.handleMouseUp);
    this.app.svg.off('mousemove', this.handleMouseMove);
  }

  removePolygonEventListeners() {
    this.app.svg.off('dblclick', this.handleDblClick);
    this.app.svg.off('dblclick', this.handlePolygonDblclick);
    this.app.svg.off('keydown', this.handlePolygonKeydown);
    this.app.svg.off('keyup', this.handlePolygonKeyup);
    if (this.shape) {
      this.shape.off('drawstart');
      this.shape.off('drawstop');
      this.shape.draw('cancel');
    }
  }

  destroy() {
    this.destroyEventListeners();
    if (this.shape) this.shape.remove();
    this.svg.attr({ cursor: 'default' });
  }
}
